import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { ExercisePattern } from '../types';
import patternService from '../services/pattern.service';
import { toast } from 'react-toastify';

interface PatternChoiceContentProps {
  onSelect: (pattern: ExercisePattern) => void;
  currentContainerId?: number | null;
}

const PatternChoiceContent: React.FC<PatternChoiceContentProps> = ({
  onSelect,
  currentContainerId,
}) => {
  const [availablePatterns, setAvailablePatterns] = useState<ExercisePattern[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAvailablePatterns();
  }, []);

  const loadAvailablePatterns = async () => {
    try {
      setLoading(true);
      const response = await patternService.getAvailablePatterns();
      setAvailablePatterns(response.results);
    } catch (error) {
      toast.error('Erreur lors du chargement des patterns disponibles');
    } finally {
      setLoading(false);
    }
  };

  const handleAdoptPattern = async (pattern: ExercisePattern) => {
    try {
      const adoptedPattern = await patternService.adoptPattern(pattern.id, currentContainerId);
      toast.success(`Pattern "${pattern.title}" ajouté à votre atelier`);
      onSelect(adoptedPattern);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du pattern');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sélectionnez un pattern existant créé par un autre utilisateur ou que vous avez précédemment supprimé.
      </Typography>
      <List sx={{ maxHeight: 400, overflow: 'auto' }}>
        {availablePatterns.map((pattern) => (
          <ListItem key={pattern.id} disablePadding>
            <ListItemButton onClick={() => handleAdoptPattern(pattern)}>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">{pattern.title}</Typography>
                    <Chip label={`ID: ${pattern.id}`} size="small" />
                  </Box>
                }
                secondary={
                  <Stack direction="row" spacing={1} mt={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Créé par: {pattern.created_by_name || 'Utilisateur'}
                    </Typography>
                    {pattern.exercise_count !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        • {pattern.exercise_count} exercices
                      </Typography>
                    )}
                    {pattern.is_reusable ? (
                      <Chip label="Réitérable" size="small" color="primary" />
                    ) : (
                      <Chip label="Non réitérable" size="small" />
                    )}
                  </Stack>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
        {availablePatterns.length === 0 && (
          <Typography variant="body2" color="text.secondary" align="center" py={4}>
            Aucun pattern disponible à ajouter
          </Typography>
        )}
      </List>
    </>
  );
};

export default PatternChoiceContent;